/* Shop page filter engine — clones the legacy /shop ProductFilter behavior.
 *
 * Server renders a plain paginated grid (works without JS). This engine
 * fetches the whole catalog slice once (?view=shop-data, best-selling order
 * = the "Popular" ranking), then filters/sorts/paginates client-side with
 * instant apply on desktop and Apply-buffered bottom sheet on mobile,
 * exactly like the legacy page. Unlike legacy, the URL is kept in sync.
 * Legacy deep-link params (category/brand/skinType/minPrice/maxPrice/
 * sortBy/query) are accepted on load.
 */
(() => {
  const root = document.querySelector('[data-shop]');
  const configEl = document.querySelector('[data-shop-config]');
  if (!root || !configEl) return;

  let cfg;
  try {
    cfg = JSON.parse(configEl.textContent);
  } catch {
    return;
  }

  const SKIN_MAP = window.MS_SHOP_SKIN || {};
  const PAGE_SIZE = cfg.pageSize || 12;
  const conversion = cfg.conversion || {};
  const i18n = cfg.i18n || {};
  const icons = cfg.icons || {};

  // ---------- DOM ----------
  const $ = (sel, el = root) => el.querySelector(sel);
  const $$ = (sel, el = root) => Array.from(el.querySelectorAll(sel));

  const grid = $('[data-shop-grid]');
  const emptyEl = $('[data-shop-empty]');
  const loadingEl = $('[data-shop-loading]');
  const moreBtn = $('[data-shop-more]');
  const chipsEl = $('[data-shop-chips]');
  const countEl = $('[data-shop-count]');
  const activeEl = $('[data-shop-active]');
  const sidebarEl = $('[data-shop-sidebar]');
  const panelEl = $('[data-shop-panel]');
  const sheetEl = $('[data-shop-sheet]');
  const sheetBody = $('[data-sheet-body]');
  const sortWrap = $('[data-shop-sort]');
  const sortToggle = $('[data-sort-toggle]');
  const sortMenu = $('[data-sort-menu]');
  const sortCurrent = $('[data-sort-current]');
  if (!grid || !panelEl) return;

  // ---------- category tree from the server-rendered panel ----------
  const catTiles = $$('[data-shop-cat]', panelEl);
  const catLevel = new Map(); // handle -> 1|2
  const catParent = new Map(); // handle -> parent handle
  catTiles.forEach((tile) => {
    catLevel.set(tile.dataset.shopCat, Number(tile.dataset.level));
    if (tile.dataset.parent) catParent.set(tile.dataset.shopCat, tile.dataset.parent);
  });
  const leaves = $$('[data-shop-leaf]', panelEl).map((el) => ({
    handle: el.dataset.shopLeaf,
    parent: el.dataset.parent,
    grandparent: el.dataset.grandparent,
    label: el.dataset.label,
    image: el.dataset.image,
  }));
  leaves.forEach((leaf) => {
    catLevel.set(leaf.handle, 3);
    catParent.set(leaf.handle, leaf.parent);
  });

  const brandTiles = $$('[data-shop-brand]', panelEl);
  const vendorBySlug = new Map();
  const slugByVendor = new Map();
  brandTiles.forEach((tile) => {
    vendorBySlug.set(tile.dataset.shopBrand, tile.dataset.vendor);
    slugByVendor.set(tile.dataset.vendor.toLowerCase(), tile.dataset.shopBrand);
  });
  const skinTiles = $$('[data-shop-skin]', panelEl);
  const skinSlugs = skinTiles.map((tile) => tile.dataset.shopSkin);

  // Localized display names for the active-filter chips, keyed by facet kind
  // so a brand slug can never shadow a category handle.
  const facetLabels = new Map();
  const tileLabel = (tile) => {
    const label = tile.querySelector('.ms-shop-tile__label');
    return ((label || tile).textContent || '').trim();
  };
  catTiles.forEach((tile) => facetLabels.set(`cat:${tile.dataset.shopCat}`, tileLabel(tile)));
  leaves.forEach((leaf) => facetLabels.set(`cat:${leaf.handle}`, leaf.label));
  brandTiles.forEach((tile) => facetLabels.set(`brand:${tile.dataset.shopBrand}`, tileLabel(tile)));
  skinTiles.forEach((tile) => facetLabels.set(`skin:${tile.dataset.shopSkin}`, tileLabel(tile)));

  const contextHandle = cfg.isShop ? null : cfg.handle;

  // ---------- state ----------
  const DEFAULT_SORT = cfg.defaultSort || 'popular'; // /newest presets newest-first, like legacy
  const emptyState = () => ({
    cat1: new Set(),
    cat2: new Set(),
    cat3: new Set(),
    brands: new Set(),
    skins: new Set(),
    priceMin: null, // BHD units
    priceMax: null,
    sort: DEFAULT_SORT,
    query: '',
  });
  const cloneState = (s) => ({
    cat1: new Set(s.cat1),
    cat2: new Set(s.cat2),
    cat3: new Set(s.cat3),
    brands: new Set(s.brands),
    skins: new Set(s.skins),
    priceMin: s.priceMin,
    priceMax: s.priceMax,
    sort: s.sort,
    query: s.query,
  });

  let applied = emptyState();
  let draft = cloneState(applied);
  // Per-device view preference — the inline boot script in main-collection
  // applies the class pre-paint; this just keeps the engine in agreement.
  let view = 'grid';
  try {
    if (localStorage.getItem('ms_shop_view') === 'list') view = 'list';
  } catch { /* private mode */ }
  let visible = PAGE_SIZE;
  let products = null; // full dataset, popularity order
  let boundsMaxDisplay = 0; // slider domain max in display currency
  let sheetOpen = false;

  const displayRate = conversion.rate > 0 ? conversion.rate : 1;
  const currencyCode = conversion.rate > 0 ? conversion.currency : (conversion.currency || 'BHD');
  const subunitsPerUnit = Math.round(1000 / (cfg.thousandUnits || 1));
  // BHD/KWD/OMR keep the 3-decimal convention even while the store money
  // format serves two — matches money-localized.liquid. Twin in ms-search.js.
  const storeDecimals = subunitsPerUnit === 1000 || /^(?:BHD|KWD|OMR)$/.test(conversion.currency || 'BHD') ? 3 : 2;

  // ---------- inbound URL ----------
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const MANAGED_PARAMS = [
    'category', 'subCategory', 'subSubCategory', 'categoryIds', 'brand', 'brandIds',
    'skinType', 'skinTypeIds', 'minPrice', 'maxPrice', 'filter.v.price.gte',
    'filter.v.price.lte', 'filter.p.vendor', 'filter.p.product_type', 'filter.p.tag',
    'filter.v.availability', 'sortBy', 'sort_by', 'q', 'query', 'page',
  ];
  const LEGACY_SORTS = {
    popularity: 'popular', sales_desc: 'popular', featured: 'popular',
    createdAt_desc: 'newest', createdAt_asc: 'oldest',
    price_asc: 'price_asc', price_desc: 'price_desc',
    'best-selling': 'popular', 'created-descending': 'newest', 'created-ascending': 'oldest',
    'price-ascending': 'price_asc', 'price-descending': 'price_desc',
  };
  const SHOPIFY_SORTS = {
    popular: 'best-selling', newest: 'created-descending', oldest: 'created-ascending',
    price_asc: 'price-ascending', price_desc: 'price-descending',
  };

  function assignCategory(value) {
    if (!value || UUID_RE.test(value)) return;
    const level = catLevel.get(value);
    if (level === 1) applied.cat1.add(value);
    else if (level === 2) {
      applied.cat2.add(value);
      const parent = catParent.get(value);
      if (parent) applied.cat1.add(parent);
    } else if (level === 3) {
      applied.cat3.add(value);
      const parent = catParent.get(value);
      if (parent) {
        applied.cat2.add(parent);
        const grand = catParent.get(parent);
        if (grand) applied.cat1.add(grand);
      }
    }
  }

  function parseFromUrl() {
    applied = emptyState();
    visible = PAGE_SIZE;
    const sp = new URLSearchParams(location.search);
    const multi = (...names) => names.flatMap((n) => sp.getAll(n)).flatMap((v) => v.split(','));
    multi('category', 'categoryIds', 'subCategory', 'subSubCategory').forEach(assignCategory);
    multi('brand', 'brandIds', 'filter.p.vendor').forEach((v) => {
      if (UUID_RE.test(v)) return;
      if (vendorBySlug.has(v)) applied.brands.add(v);
      else if (slugByVendor.has(v.toLowerCase())) applied.brands.add(slugByVendor.get(v.toLowerCase()));
    });
    multi('skinType', 'skinTypeIds').forEach((v) => {
      if (skinSlugs.includes(v)) applied.skins.add(v);
    });
    const gte = parseFloat(sp.get('filter.v.price.gte') ?? sp.get('minPrice'));
    const lte = parseFloat(sp.get('filter.v.price.lte') ?? sp.get('maxPrice'));
    if (Number.isFinite(gte) && gte > 0) applied.priceMin = gte;
    if (Number.isFinite(lte)) applied.priceMax = lte;
    const sortRaw = sp.get('sortBy') || sp.get('sort_by');
    if (sortRaw && LEGACY_SORTS[sortRaw]) applied.sort = LEGACY_SORTS[sortRaw];
    applied.query = (sp.get('q') || sp.get('query') || '').trim();
    const page = parseInt(sp.get('page') || '1', 10);
    if (Number.isFinite(page) && page > 1) visible = page * PAGE_SIZE;
    draft = cloneState(applied);
  }
  parseFromUrl();

  function hasActiveState(s) {
    return (
      s.cat1.size || s.cat2.size || s.cat3.size || s.brands.size || s.skins.size ||
      s.priceMin !== null || s.priceMax !== null || s.sort !== DEFAULT_SORT || s.query
    );
  }

  // ---------- money ----------
  // The Saudi riyal mark exactly as snippets/money-localized.liquid emits it,
  // so a client-rendered price is the same markup as a server-rendered one.
  const riyalMarkHtml = () => `<img src="${conversion.riyalMark}" alt="${conversion.currency}" width="16" height="16" style="display: inline; height: .95em; max-height: 1.05em; margin-inline-start: .2em; object-fit: contain; vertical-align: middle; width: auto;">`;

  const formatMoney = (subunits) => {
    const base = subunits / subunitsPerUnit;
    if (conversion.rate > 0) {
      const converted = (base * conversion.rate).toFixed(conversion.decimals);
      if (conversion.riyalMark) {
        return `<span dir="ltr">${converted}</span>${riyalMarkHtml()}`;
      }
      if (conversion.isArabic) return `<span dir="ltr">${converted}</span>&nbsp;${conversion.label}`;
      return `${converted} ${conversion.currency}`;
    }
    const amount = base.toFixed(storeDecimals);
    if (conversion.isArabic && conversion.label) {
      return `<span dir="ltr">${amount}</span>&nbsp;${conversion.label}`;
    }
    return (cfg.moneyFormat || '{{ amount }}').replace(/\{\{\s*amount[^}]*\}\}/, amount).replace(/<[^>]+>/g, '');
  };

  /* The price slider works in DISPLAY currency, already converted, so it
     cannot go through formatMoney (which takes store subunits). This is the
     presentation half of formatMoney on its own — without it the two range
     labels printed a raw "SAR 8316.00" next to product cards showing the
     riyal mark, and .toFixed(2) lost BHD's third decimal. */
  const formatDisplayAmount = (value) => {
    const decimals = conversion.rate > 0
      ? conversion.decimals
      : storeDecimals;
    const amount = Number(value || 0).toFixed(decimals);
    if (conversion.rate > 0 && conversion.riyalMark) {
      return `<span dir="ltr">${amount}</span>${riyalMarkHtml()}`;
    }
    if (conversion.isArabic && conversion.label) {
      return `<span dir="ltr">${amount}</span>&nbsp;${conversion.label}`;
    }
    return `${amount} ${currencyCode}`;
  };

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  // ---------- dataset ----------
  // v2: the feed gained benefit/ratingScale ("schema": 2) — a dataset cached
  // under the old key would render cards without a benefit line.
  const cacheKey = `msShopData:v2:${cfg.locale}:${cfg.handle}`;
  async function loadData() {
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
      if (cached && Date.now() - cached.t < 600000 && Array.isArray(cached.products)) {
        return cached.products;
      }
    } catch { /* ignore */ }

    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    const out = [];
    let page = 1;
    let pages = 1;
    do {
      let url = `${cfg.dataUrl}&page=${page}`;
      if (preview) url += `&preview_theme_id=${preview}`;
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`shop-data HTTP ${res.status}`);
      const data = await res.json();
      out.push(...data.products);
      pages = data.pages || 1;
      page += 1;
    } while (page <= pages && page <= 12);

    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({ t: Date.now(), products: out }));
    } catch { /* storage full/private — fine */ }
    return out;
  }

  // ---------- filtering ----------
  const effectiveUnits = (p) => p.price / subunitsPerUnit; // store-currency units (BHD)

  function activeCategoryHandles(s) {
    // Deepest selection wins per branch, mirroring the legacy refinement.
    const set = new Set();
    s.cat3.forEach((h) => set.add(h));
    s.cat2.forEach((h) => {
      const refined = [...s.cat3].some((leaf) => catParent.get(leaf) === h);
      if (!refined) set.add(h);
    });
    s.cat1.forEach((h) => {
      const refined = [...s.cat2].some((sub) => catParent.get(sub) === h);
      if (!refined) set.add(h);
    });
    return set;
  }

  // Visible facet options — recomputed by pruneFacets() once the dataset arrives.
  let visibleBrandSlugs = brandTiles.map((t) => t.dataset.shopBrand);
  let visibleSkinSlugs = skinTiles.map((t) => t.dataset.shopSkin);
  const isAllSelected = (set, slugs) => slugs.length > 0 && slugs.every((slug) => set.has(slug));

  // Fold Arabic orthographic variants so hamza-less queries still match
  // (اوسم finds أوسم, مجموعه finds مجموعة). Latin text passes through
  // untouched. Twin of foldArabic in ms-search.js — keep them identical.
  const foldArabic = (value) => value
    .replace(/[\u0622\u0623\u0625]/g, '\u0627') // آ أ إ -> ا
    .replace(/\u0629/g, '\u0647') // ة -> ه
    .replace(/\u0649/g, '\u064a') // ى -> ي
    .replace(/[\u0640\u064b-\u065f\u0670]/g, ''); // tatweel + diacritics

  function matches(p, s, { ignorePrice = false } = {}) {
    if (!p.available) return false; // legacy always sends inStock=true
    const cats = activeCategoryHandles(s);
    if (cats.size) {
      let hit = false;
      for (const h of p.collections) if (cats.has(h)) { hit = true; break; }
      if (!hit) return false;
    }
    // "ALL" selected = no narrowing: never show fewer products than no filter.
    if (s.brands.size && !isAllSelected(s.brands, visibleBrandSlugs)) {
      const slug = slugByVendor.get((p.vendor || '').toLowerCase());
      if (!slug || !s.brands.has(slug)) return false;
    }
    if (s.skins.size && !isAllSelected(s.skins, visibleSkinSlugs)) {
      const skins = SKIN_MAP[p.handle];
      if (!skins || !skins.some((slug) => s.skins.has(slug))) return false;
    }
    if (!ignorePrice) {
      const units = effectiveUnits(p);
      if (s.priceMin !== null && units < s.priceMin) return false;
      if (s.priceMax !== null && units > s.priceMax) return false;
    }
    if (s.query) {
      // Same matcher as the search dropdown (ms-search.js): every whitespace
      // term must hit the localized title, vendor, or the English handle —
      // so "osm" finds products on the Arabic pages too, and the grid always
      // agrees with the dropdown's "view all (N)" count.
      const haystack = foldArabic(`${p.title} ${p.vendor || ''} ${p.handle}`.toLowerCase());
      const terms = foldArabic(s.query.toLowerCase()).split(/\s+/).filter(Boolean);
      if (!terms.every((term) => haystack.includes(term))) return false;
    }
    return true;
  }

  function filteredProducts(s) {
    if (!products) return [];
    let list = products.filter((p) => matches(p, s));
    switch (s.sort) {
      case 'newest': list = list.slice().sort((a, b) => b.created - a.created); break;
      case 'oldest': list = list.slice().sort((a, b) => a.created - b.created); break;
      case 'price_asc': list = list.slice().sort((a, b) => a.price - b.price); break;
      case 'price_desc': list = list.slice().sort((a, b) => b.price - a.price); break;
      default: break; // popular = dataset order (best-selling)
    }
    return list;
  }

  // ---------- price slider ----------
  const priceInputs = {
    min: $('[data-price-input="min"]', panelEl),
    max: $('[data-price-input="max"]', panelEl),
  };
  const priceLabels = {
    min: $('[data-price-label="min"]', panelEl),
    max: $('[data-price-label="max"]', panelEl),
  };
  const priceTrack = $('[data-price-track]', panelEl);

  const toDisplay = (units) => units * displayRate;
  const toUnits = (display) => display / displayRate;

  function refreshPriceBounds() {
    if (!products || !priceInputs.min) return;
    const candidates = products.filter((p) => matches(p, draft, { ignorePrice: true }));
    // No products to bound = no price facet, like the empty brand/skin
    // sections in refreshFacetOptions.
    const priceSection = $('[data-facet="price"]', panelEl);
    if (priceSection) priceSection.hidden = !candidates.length;
    let maxUnits = 0;
    candidates.forEach((p) => { maxUnits = Math.max(maxUnits, effectiveUnits(p)); });
    // A committed range that exceeds the new bounds would filter invisibly —
    // drop it (legacy refetches bounds and shows the slider full-range too).
    if (draft.priceMax !== null && draft.priceMax >= maxUnits) draft.priceMax = null;
    if (draft.priceMin !== null && draft.priceMin > maxUnits) draft.priceMin = null;
    // Step-align the domain so the max thumb can actually reach it (range
    // inputs clamp values to step multiples — an unreachable max would turn
    // every drag into a phantom max-price filter).
    const rawMax = Math.ceil(toDisplay(maxUnits));
    const step = rawMax > 100 ? Math.ceil(rawMax / 200) : 1;
    boundsMaxDisplay = Math.ceil(rawMax / step) * step;
    ['min', 'max'].forEach((k) => {
      priceInputs[k].min = 0;
      priceInputs[k].max = boundsMaxDisplay;
      priceInputs[k].step = step;
    });
    const trueMin = draft.priceMin !== null ? toDisplay(draft.priceMin) : 0;
    const trueMax = draft.priceMax !== null ? toDisplay(draft.priceMax) : boundsMaxDisplay;
    priceInputs.min.value = Math.min(Math.round(trueMin / step) * step, boundsMaxDisplay);
    priceInputs.max.value = Math.min(Math.max(Math.round(trueMax / step) * step, Number(priceInputs.min.value)), boundsMaxDisplay);
    // Labels show the TRUE committed range, not the step-snapped thumb value.
    refreshPriceUi(trueMin, trueMax);
  }

  // The two thumbs may cross mid-drag (see the input handler), so the range
  // is always the lower and the higher of the two, whichever thumb holds it.
  const sliderRange = () => {
    const a = Number(priceInputs.min.value);
    const b = Number(priceInputs.max.value);
    return [Math.min(a, b), Math.max(a, b)];
  };

  function refreshPriceUi(trueLo, trueHi) {
    if (!priceInputs.min) return;
    const [thumbLo, thumbHi] = sliderRange();
    const lo = typeof trueLo === 'number' ? trueLo : thumbLo;
    const hi = typeof trueHi === 'number' ? trueHi : thumbHi;
    const domain = Math.max(1, Number(priceInputs.min.max));
    if (priceTrack) {
      const startPct = Math.min(100, (lo / domain) * 100);
      const endPct = Math.min(100, (hi / domain) * 100);
      priceTrack.style.insetInlineStart = `${startPct}%`;
      priceTrack.style.width = `${Math.max(0, endPct - startPct)}%`;
    }
    if (priceLabels.min) priceLabels.min.innerHTML = formatDisplayAmount(lo);
    if (priceLabels.max) priceLabels.max.innerHTML = formatDisplayAmount(hi);
  }

  function commitPriceFromSlider() {
    const [lo, hi] = sliderRange();
    const domainMax = Number(priceInputs.max.max);
    draft.priceMin = lo > 0 ? toUnits(lo) : null;
    draft.priceMax = hi < domainMax ? toUnits(hi) : null;
    sync();
  }

  let priceDebounce = null;
  const schedulePriceCommit = () => {
    clearTimeout(priceDebounce);
    priceDebounce = setTimeout(commitPriceFromSlider, 400); // legacy debounce
  };

  // After a drag, give the thumbs back their names: the min input holds the
  // lower value again. Both values are already where the thumbs are drawn,
  // so nothing moves on screen.
  const reorderThumbs = () => {
    const [lo, hi] = sliderRange();
    priceInputs.min.value = lo;
    priceInputs.max.value = hi;
  };

  ['min', 'max'].forEach((k) => {
    const input = priceInputs[k];
    if (!input) return;

    // Pointer drags. The inputs are stacked and the max one is on top, so
    // once the thumbs meet every grab takes the MAX thumb. Pushing the other
    // thumb along (the old rule) meant dragging lower dragged both and the
    // range collapsed to zero width; instead the thumbs may pass each other
    // and the range is simply lower..higher — dragging down from a meeting
    // point opens the range downwards, as it should.
    input.addEventListener('input', () => {
      refreshPriceUi();
      schedulePriceCommit();
    });
    input.addEventListener('change', () => {
      reorderThumbs();
      clearTimeout(priceDebounce);
      commitPriceFromSlider();
    });

    // Keys are handled here rather than by the browser. Safari does not
    // mirror the arrow keys on a right-to-left range input (Chrome does), so
    // on Arabic pages Left moved the thumb right. Here the Left/Right arrows
    // move the thumb that way on screen in both engines and both directions,
    // Up/Page Up raise the value and Down/Page Down lower it, Home/End go to
    // the ends — and a thumb stops at the other thumb instead of crossing.
    input.addEventListener('keydown', (event) => {
      const rtl = getComputedStyle(input).direction === 'rtl';
      const step = Number(input.step) || 1;
      const low = Number(input.min) || 0;
      const high = Number(input.max) || 0;
      const page = Math.max(step, Math.round((high - low) / 10 / step) * step);
      const [lo, hi] = sliderRange();
      const floor = k === 'min' ? low : lo;
      const ceil = k === 'min' ? hi : high;
      const value = Number(input.value);
      const moves = {
        ArrowRight: rtl ? -step : step,
        ArrowLeft: rtl ? step : -step,
        ArrowUp: step,
        ArrowDown: -step,
        PageUp: page,
        PageDown: -page,
      };
      let next;
      if (event.key === 'Home') next = floor;
      else if (event.key === 'End') next = ceil;
      else if (event.key in moves) next = value + moves[event.key];
      else return;
      event.preventDefault();
      next = Math.min(ceil, Math.max(floor, next));
      if (next === value) return;
      input.value = next;
      refreshPriceUi();
      schedulePriceCommit();
    });
  });

  // ---------- card rendering ----------
  // Twin of snippets/product-card.liquid: same element nesting and class
  // names, in the same order, so a card keeps its shape when the grid is
  // re-rendered after a filter/sort. Change one, change the other.
  // The feed (templates/collection.shop-data.liquid) supplies every field.

  // "New" without a tag: published in the last 60 days (the Liquid card's rule).
  const NEW_WINDOW_SECONDS = 60 * 24 * 60 * 60;
  // Arabic letters (hamza..yeh) — the Liquid card's letter list lives in
  // this range. A title with none of them gets the --latin (LTR) class.
  const ARABIC_LETTER_RE = /[ء-ي]/;

  function badgeData(p) {
    const tags = (p.tags || []).map((t) => String(t).toLowerCase());
    const soon = tags.includes('coming soon');
    const best = tags.includes('best seller');
    const price = Number(p.price) || 0;
    const compare = Number(p.compare) || 0;
    const onSale = compare > price;
    const percent = onSale ? Math.round(((compare - price) * 100) / compare) : 0;
    const created = Number(p.created) || 0;
    const isNew = tags.includes('new') || (created > 0 && created > Date.now() / 1000 - NEW_WINDOW_SECONDS);
    // One badge; priority soon > offer > best > new.
    let badge = '';
    if (soon) badge = 'soon';
    else if (onSale) badge = 'offer';
    else if (best) badge = 'best';
    else if (isNew) badge = 'new';
    return { soon, best, isNew, onSale, percent, badge };
  }

  function badgesHtml(b) {
    if (!b.badge) return '';
    let text = '';
    if (b.badge === 'soon') text = i18n.badgeSoon || '';
    else if (b.badge === 'offer') text = (i18n.badgePercentOff || '@@%').replace('@@', b.percent);
    else if (b.badge === 'best') text = i18n.badgeBest || '';
    else text = i18n.badgeNew || '';
    return '<span class="ms-product-card__badges">'
      + `<span class="ms-product-card__badge ms-product-card__badge--${b.badge}">${escapeHtml(text)}</span>`
      + '</span>';
  }

  function wishlistHtml(p) {
    return `<button type="button" class="ms-product-card__wishlist" data-ms-wishlist-toggle data-handle="${escapeHtml(p.handle)}" aria-pressed="false" aria-label="${escapeHtml(i18n.wishlist)}">${icons.heart || ''}</button>`;
  }

  // Same wrapper snippets/money-localized.liquid puts around an amount.
  function moneyHtml(subunits) {
    const attrs = conversion.isArabic
      ? ' style="white-space: nowrap; text-decoration: inherit;"'
      : ' dir="ltr"';
    return `<span class="ms-money"${attrs}>${formatMoney(subunits)}</span>`;
  }

  // CLDR plural form for a count, from the per-form strings the config
  // carries (same trick as refreshCount). `pluralRules` is defined further
  // down; cards only render after the whole module has booted.
  function pluralPick(forms, count) {
    const table = forms || {};
    const form = pluralRules ? pluralRules.select(count) : (count === 1 ? 'one' : 'other');
    return (table[form] || table.other || '').replace('@@', count);
  }

  function ratingHtml(p) {
    const count = Number(p.ratingCount) || 0;
    const raw = Number(p.rating);
    if (!(count > 0) || !Number.isFinite(raw) || raw <= 0) return '';
    const score = Math.round(raw * 10) / 10;
    const scale = Number(p.ratingScale) > 0 ? Number(p.ratingScale) : 5;
    const percent = Math.round((score / scale) * 100);
    const shown = score.toFixed(1);
    const label = (i18n.ratingLabel || '@@rating / @@total').replace('@@rating', shown).replace('@@total', count);
    return `<span class="ms-product-card__rating" aria-label="${escapeHtml(label)}">`
      + `<span class="ms-product-card__stars" aria-hidden="true" style="--ms-stars: ${percent}%">`
      + '<span class="ms-product-card__stars-fill">★★★★★</span>★★★★★'
      + '</span>'
      + `<b class="ms-product-card__rating-score" dir="ltr">${shown}</b>`
      + `<small class="ms-product-card__rating-count">(${escapeHtml(pluralPick(i18n.reviewsCount, count))})</small>`
      + '</span>';
  }

  function cartQuantityFor(variantId) {
    try {
      const line = window.MSCart && typeof window.MSCart.lineFor === 'function'
        ? window.MSCart.lineFor(variantId)
        : null;
      return line && Number.isFinite(line.quantity) ? line.quantity : 0;
    } catch {
      return 0;
    }
  }

  // The card's CTA. One available variant: the quick-add control (the cart
  // controller in sections/cart-drawer.liquid owns the data hooks). Several
  // variants or sold out: the same button as a plain link to the PDP.
  // Coming soon: nothing.
  function quickAddHtml(p, b) {
    if (b.soon) return '';
    const url = escapeHtml(p.url);
    const icon = `<span class="ms-quick-add__icon" aria-hidden="true">${icons.cart || ''}</span>`;
    const label = `<span class="ms-quick-add__label">${escapeHtml(i18n.shopNow || i18n.addToCart || '')}</span>`;
    if (Number(p.variantCount) === 1 && p.variantAvailable && p.variantId) {
      const qty = cartQuantityFor(p.variantId);
      return `<div class="ms-quick-add" data-ms-quick-add data-variant-id="${escapeHtml(p.variantId)}" data-quantity="${qty}" data-available="true">`
        + `<a class="ms-quick-add__btn" href="${url}" data-ms-quick-trigger>`
        + icon
        + `<span class="ms-quick-add__count" data-ms-quick-value aria-hidden="true">${qty}</span>`
        + label
        + '</a>'
        + '<div class="ms-cart-qty ms-quick-add__stepper" data-ms-quick-stepper hidden>'
        + `<button type="button" class="ms-cart-qty__btn" data-ms-quick-step="-1" aria-label="${escapeHtml(i18n.qtyDecrease || '')}">${icons.minus || '−'}</button>`
        + `<span class="ms-cart-qty__value" data-ms-quick-value>${qty}</span>`
        + `<button type="button" class="ms-cart-qty__btn" data-ms-quick-step="1" aria-label="${escapeHtml(i18n.qtyIncrease || '')}">${icons.plus || '+'}</button>`
        + '</div></div>';
    }
    return `<a class="ms-quick-add__btn ms-quick-add__btn--link" href="${url}">${icon}${label}</a>`;
  }

  function srcsetFor(url) {
    if (!url.includes('width=600')) return '';
    const widths = [200, 300, 400, 600];
    return widths.map((w) => `${escapeHtml(url.replace('width=600', `width=${w}`))} ${w}w`).join(', ');
  }

  function imageHtml(p) {
    if (!p.image) {
      // The Liquid card draws placeholder_svg_tag here; an empty svg with the
      // same class keeps the nesting (the media well shows through).
      return '<svg class="ms-product-card__placeholder" aria-hidden="true"></svg>';
    }
    const sizes = '(min-width: 900px) 25vw, 76vw';
    const primary = `<img class="ms-product-card__image ms-product-card__image--primary" src="${escapeHtml(p.image)}" srcset="${srcsetFor(p.image)}" sizes="${sizes}" alt="${escapeHtml(p.imageAlt)}" loading="lazy" width="600" height="600">`;
    const hover = p.hoverImage
      ? `<img class="ms-product-card__image ms-product-card__image--hover" src="${escapeHtml(p.hoverImage)}" srcset="${srcsetFor(p.hoverImage)}" sizes="${sizes}" alt="${escapeHtml(p.imageAlt)}" loading="lazy" width="600" height="600">`
      : '';
    return primary + hover;
  }

  function cardHtml(p) {
    const b = badgeData(p);
    const classes = ['ms-product-card'];
    if (b.onSale) classes.push('ms-product-card--sale');
    if (p.hoverImage) classes.push('ms-product-card--dual');
    const url = escapeHtml(p.url);
    const title = escapeHtml(p.title);
    const vendor = String(p.vendor || '').trim();
    const benefit = String(p.benefit || '').trim();
    const titleClass = ARABIC_LETTER_RE.test(String(p.title || ''))
      ? 'ms-product-card__title'
      : 'ms-product-card__title ms-product-card__title--latin';
    // The compare line is always present so a discounted card is exactly as
    // tall as one without an offer.
    const compare = b.onSale
      ? `<span class="ms-product-card__compare">${moneyHtml(p.compare)}</span>`
      : '<span class="ms-product-card__compare ms-product-card__compare--empty" aria-hidden="true">&nbsp;</span>';
    return `<article class="${classes.join(' ')}">`
      + (b.soon ? '' : wishlistHtml(p))
      + `<a class="ms-product-card__media" href="${url}">`
      + `<span class="visually-hidden">${title}</span>`
      + `<span class="ms-product-card__media-inner">${imageHtml(p)}</span>${badgesHtml(b)}</a>`
      + '<div class="ms-product-card__info">'
      + (vendor ? `<span class="ms-product-card__vendor">${escapeHtml(vendor)}</span>` : '')
      + `<h2 class="${titleClass}"><a href="${url}">${title}</a></h2>`
      + (benefit ? `<p class="ms-product-card__benefit">${escapeHtml(benefit)}</p>` : '')
      + `<div class="ms-product-card__actions">${ratingHtml(p)}</div>`
      + `<div class="ms-product-card__prices"><p class="ms-product-card__price">${moneyHtml(p.price)}</p>${compare}</div>`
      + quickAddHtml(p, b)
      + '</div></article>';
  }

  function skeletonHtml(count) {
    let out = '';
    for (let i = 0; i < count; i += 1) {
      out += '<div class="ms-shop-skel"><div class="ms-shop-skel__img"></div><div class="ms-shop-skel__line"></div><div class="ms-shop-skel__line ms-shop-skel__line--short"></div><div class="ms-shop-skel__pill"></div></div>';
    }
    return out;
  }

  // ---------- panel UI refresh ----------
  function refreshTiles() {
    refreshFacetOptions();
    catTiles.forEach((tile) => {
      const h = tile.dataset.shopCat;
      const level = Number(tile.dataset.level);
      const set = level === 1 ? draft.cat1 : draft.cat2;
      const active = set.has(h);
      tile.classList.toggle('is-active', active);
      tile.setAttribute('aria-checked', String(active));
    });
    // sub-category groups appear when their parent is selected (or is the page context)
    $$('[data-cat-group]', panelEl).forEach((group) => {
      const parent = group.dataset.catGroup;
      group.hidden = !(draft.cat1.has(parent) || contextHandle === parent);
      // Legacy qualifies the heading with the parent name only when more
      // than one parent is selected ("Sub Categories (Makeup)").
      const context = group.querySelector('[data-group-context]');
      if (context) context.hidden = draft.cat1.size < 2;
    });
    brandTiles.forEach((tile) => {
      const active = draft.brands.has(tile.dataset.shopBrand);
      tile.classList.toggle('is-active', active);
      tile.setAttribute('aria-checked', String(active));
    });
    skinTiles.forEach((tile) => {
      const active = draft.skins.has(tile.dataset.shopSkin);
      tile.classList.toggle('is-active', active);
      tile.setAttribute('aria-checked', String(active));
    });
    const allBrand = $('[data-shop-all="brand"]', panelEl);
    if (allBrand) {
      const allOn = visibleBrandSlugs.length > 0 && visibleBrandSlugs.every((s) => draft.brands.has(s));
      allBrand.classList.toggle('is-active', allOn);
      allBrand.setAttribute('aria-checked', String(allOn));
    }
    const allSkin = $('[data-shop-all="skin"]', panelEl);
    if (allSkin) {
      const allOn = visibleSkinSlugs.length > 0 && visibleSkinSlugs.every((s) => draft.skins.has(s));
      allSkin.classList.toggle('is-active', allOn);
      allSkin.setAttribute('aria-checked', String(allOn));
    }
    refreshChips();
    refreshPriceBounds();
  }

  function chipContextParent() {
    // the single selected sub-category whose leaves feed the chip strip
    if (contextHandle && catLevel.get(contextHandle) === 2) return contextHandle;
    if (contextHandle && catLevel.get(contextHandle) === 1) {
      // On a level-1 collection page the root facet is hidden, so cat1 stays
      // empty — a single selected child of the context still gets its chips.
      if (draft.cat2.size === 1 && catParent.get([...draft.cat2][0]) === contextHandle) {
        return [...draft.cat2][0];
      }
      return null;
    }
    if (draft.cat1.size === 1 && draft.cat2.size === 1) return [...draft.cat2][0];
    return null;
  }

  function refreshChips() {
    if (!chipsEl) return;
    const parent = chipContextParent();
    const items = parent ? leaves.filter((leaf) => leaf.parent === parent) : [];
    if (!items.length) {
      chipsEl.hidden = true;
      chipsEl.innerHTML = '';
      return;
    }
    chipsEl.hidden = false;
    chipsEl.innerHTML = items.map((leaf) => {
      const active = draft.cat3.has(leaf.handle);
      const img = leaf.image
        ? `<img src="${escapeHtml(leaf.image)}" width="44" height="44" loading="lazy" alt="">`
        : `<span class="ms-shop-tile__glyph" aria-hidden="true">${icons.grid || ''}</span>`;
      return `<button type="button" class="ms-shop-tile${active ? ' is-active' : ''}" data-chip-leaf="${escapeHtml(leaf.handle)}" role="checkbox" aria-checked="${active}">`
        + `<span class="ms-shop-tile__img">${img}</span>`
        + `<span class="ms-shop-tile__label">${escapeHtml(leaf.label)}</span></button>`;
    }).join('');
  }

  function refreshSortUi() {
    if (sortCurrent) sortCurrent.textContent = (i18n.sortLabels || {})[draft.sort] || '';
    $$('[data-sort]', sortWrap || root).forEach((opt) => {
      const active = opt.dataset.sort === draft.sort;
      opt.classList.toggle('is-active', active);
      opt.setAttribute('aria-checked', String(active));
    });
  }

  // ---------- toolbar: results count / active chips / view toggle ----------
  const pluralRules = (() => {
    try {
      return new Intl.PluralRules(cfg.locale);
    } catch {
      return null;
    }
  })();

  function refreshCount(count) {
    if (!countEl) return;
    const forms = i18n.resultsCount || {};
    const form = pluralRules ? pluralRules.select(count) : (count === 1 ? 'one' : 'other');
    countEl.textContent = (forms[form] || forms.other || '').replace('@@', count);
    countEl.hidden = false; // SSR keeps it hidden — its number counts unavailable products too
  }

  // Chips describe the APPLIED state (the grid on screen), never a buffered
  // sheet draft; removal goes through the same draft mutation + sync() path
  // as the facet panel, so history and pruning behave identically.
  function renderActiveChips() {
    if (!activeEl) return;
    const items = [];
    activeCategoryHandles(applied).forEach((h) => {
      items.push({ kind: 'cat', value: h, level: catLevel.get(h), label: facetLabels.get(`cat:${h}`) || h });
    });
    // Every visible tile selected means "no narrowing" (the ALL tile) —
    // chips for that facet would imply a filter that is not filtering.
    if (!isAllSelected(applied.brands, visibleBrandSlugs)) {
      applied.brands.forEach((slug) => items.push({ kind: 'brand', value: slug, label: facetLabels.get(`brand:${slug}`) || slug }));
    }
    if (!isAllSelected(applied.skins, visibleSkinSlugs)) {
      applied.skins.forEach((slug) => items.push({ kind: 'skin', value: slug, label: facetLabels.get(`skin:${slug}`) || slug }));
    }
    if (applied.priceMin !== null || applied.priceMax !== null) {
      const lo = applied.priceMin !== null ? toDisplay(applied.priceMin) : 0;
      const hi = applied.priceMax !== null ? toDisplay(applied.priceMax) : boundsMaxDisplay;
      items.push({
        kind: 'price',
        value: 'price',
        label: i18n.priceLabel || '',
        html: `${formatDisplayAmount(lo)}&nbsp;&ndash;&nbsp;${formatDisplayAmount(hi)}`,
      });
    }
    activeEl.hidden = !items.length;
    if (!items.length) {
      activeEl.innerHTML = '';
      return;
    }
    let out = items.map((item) => {
      const remove = (i18n.removeFilter || '@@').replace('@@', item.label);
      return `<button type="button" class="ms-shop-chip" data-active-chip data-kind="${item.kind}" data-value="${escapeHtml(item.value)}"${item.level ? ` data-level="${item.level}"` : ''} aria-label="${escapeHtml(remove)}">`
        + `<span class="ms-shop-chip__label">${item.html || escapeHtml(item.label)}</span>`
        + '<span class="ms-shop-chip__x" aria-hidden="true">&times;</span></button>';
    }).join('');
    if (items.length >= 2) {
      out += `<button type="button" class="ms-shop-chip ms-shop-chip--clear" data-active-clear>${escapeHtml(i18n.clearAll)}</button>`;
    }
    activeEl.innerHTML = out;
  }

  // Both views render the same card markup — list is pure CSS on the root
  // class, so switching never rebuilds the grid (and works pre-hydration).
  function applyView() {
    root.classList.toggle('ms-shop--list', view === 'list');
    $$('[data-view]', root).forEach((btn) => {
      const active = btn.dataset.view === view;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
    });
  }

  // ---------- URL sync ----------
  // Each committed state change is its own history entry so Back walks the
  // shopper through their filter/sort/page steps instead of off the page.
  // Off until the boot render (which only canonicalizes the landing URL) and
  // while popstate restores an old entry.
  let urlPushEnabled = false;
  function syncUrl() {
    const sp = new URLSearchParams(location.search);
    MANAGED_PARAMS.forEach((k) => sp.delete(k));
    applied.cat1.forEach((h) => sp.append('category', h));
    applied.cat2.forEach((h) => sp.append('subCategory', h));
    applied.cat3.forEach((h) => sp.append('subSubCategory', h));
    applied.brands.forEach((s) => sp.append('brand', s));
    applied.skins.forEach((s) => sp.append('skinType', s));
    if (applied.priceMin !== null && applied.priceMin > 0) sp.set('filter.v.price.gte', applied.priceMin.toFixed(3));
    if (applied.priceMax !== null) sp.set('filter.v.price.lte', applied.priceMax.toFixed(3));
    if (applied.sort !== DEFAULT_SORT) sp.set('sort_by', SHOPIFY_SORTS[applied.sort]);
    if (applied.query) sp.set('q', applied.query);
    if (visible > PAGE_SIZE) sp.set('page', String(Math.ceil(visible / PAGE_SIZE)));
    const qs = sp.toString();
    const target = location.pathname + (qs ? `?${qs}` : '') + location.hash;
    if (target === location.pathname + location.search + location.hash) return;
    if (urlPushEnabled) history.pushState(null, '', target);
    else history.replaceState(null, '', target);
  }

  window.addEventListener('popstate', () => {
    // Restore the state the popped entry's URL describes — without pushing.
    if (sheetOpen) {
      sheetSnapshot = null;
      closeSheet(false);
    }
    parseFromUrl();
    const wasPush = urlPushEnabled;
    urlPushEnabled = false;
    refreshSortUi();
    refreshTiles();
    if (products) render();
    urlPushEnabled = wasPush;
  });

  // Keep the server-rendered first page until the shopper filters, sorts or
  // pages (the view toggle is pure CSS, so it never replaces the grid).
  // Replacing that grid on boot was the largest CLS on Best Selling (the
  // whole card row swapped after layout).
  let ssrGridKept = false;
  function render() {
    if (!products) return;
    const list = filteredProducts(applied);
    const slice = list.slice(0, visible);
    loadingEl.hidden = true;
    grid.hidden = false;
    const keepSsr =
      !ssrGridKept &&
      !hasActiveState(applied) &&
      visible <= PAGE_SIZE &&
      grid.childElementCount > 0;
    ssrGridKept = true;
    if (!keepSsr) {
      grid.innerHTML = slice.map(cardHtml).join('');
    }
    if (emptyEl) emptyEl.hidden = list.length > 0;
    if (moreBtn) moreBtn.hidden = visible >= list.length;
    refreshCount(list.length);
    renderActiveChips();
    if (window.MSWishlist && typeof window.MSWishlist.sync === 'function') window.MSWishlist.sync();
    syncUrl();
  }

  // ---------- commit ----------
  let preDataCommit = false; // a commit made before the dataset arrived
  function sync() {
    // A leaf (level-3) selection must never outlive its chip strip — once the
    // strip's context changes there is no control on screen to remove it.
    const chipParent = chipContextParent();
    [...draft.cat3].forEach((leaf) => {
      if (catParent.get(leaf) !== chipParent) draft.cat3.delete(leaf);
    });
    refreshTiles();
    refreshSortUi();
    if (sheetOpen) return; // buffered until Apply
    if (!products) preDataCommit = true; // boot will push its entry
    applied = cloneState(draft);
    visible = PAGE_SIZE;
    render();
  }

  // ---------- interactions ----------
  function toggleCat(handle, level) {
    const set = level === 1 ? draft.cat1 : level === 2 ? draft.cat2 : draft.cat3;
    if (set.has(handle)) {
      set.delete(handle);
      if (level === 1) {
        // cascade: drop child selections of this parent
        [...draft.cat2].forEach((sub) => {
          if (catParent.get(sub) === handle) {
            draft.cat2.delete(sub);
            [...draft.cat3].forEach((leaf) => {
              if (catParent.get(leaf) === sub) draft.cat3.delete(leaf);
            });
          }
        });
      }
      if (level === 2) {
        [...draft.cat3].forEach((leaf) => {
          if (catParent.get(leaf) === handle) draft.cat3.delete(leaf);
        });
      }
    } else {
      set.add(handle);
    }
    sync();
  }

  root.addEventListener('click', (event) => {
    const catTile = event.target.closest('[data-shop-cat]');
    if (catTile && panelEl.contains(catTile)) {
      // Without a dataset the tiles fall back to their server hrefs.
      if (!products) return;
      event.preventDefault();
      toggleCat(catTile.dataset.shopCat, Number(catTile.dataset.level));
      return;
    }

    const chip = event.target.closest('[data-chip-leaf]');
    if (chip) {
      toggleCat(chip.dataset.chipLeaf, 3);
      return;
    }

    const brandTile = event.target.closest('[data-shop-brand]');
    if (brandTile) {
      // Without a dataset a highlight would filter nothing — false feedback.
      if (!products) return;
      const slug = brandTile.dataset.shopBrand;
      if (draft.brands.has(slug)) draft.brands.delete(slug);
      else draft.brands.add(slug);
      sync();
      return;
    }

    const skinTile = event.target.closest('[data-shop-skin]');
    if (skinTile) {
      if (!products) return;
      const slug = skinTile.dataset.shopSkin;
      if (draft.skins.has(slug)) draft.skins.delete(slug);
      else draft.skins.add(slug);
      sync();
      return;
    }

    const allTile = event.target.closest('[data-shop-all]');
    if (allTile) {
      if (!products) return;
      const kind = allTile.dataset.shopAll;
      const tiles = kind === 'brand' ? brandTiles : skinTiles;
      const set = kind === 'brand' ? draft.brands : draft.skins;
      const slugs = tiles.filter((t) => !t.hidden).map((t) => kind === 'brand' ? t.dataset.shopBrand : t.dataset.shopSkin);
      const allOn = slugs.length > 0 && slugs.every((s) => set.has(s));
      set.clear();
      if (!allOn) slugs.forEach((s) => set.add(s));
      sync();
      return;
    }

    const clearBtn = event.target.closest('[data-shop-clear]');
    if (clearBtn) {
      draft = emptyState();
      sync();
      return;
    }

    const activeChip = event.target.closest('[data-active-chip]');
    if (activeChip) {
      const { kind, value } = activeChip.dataset;
      if (kind === 'cat') {
        toggleCat(value, Number(activeChip.dataset.level));
        return;
      }
      if (kind === 'brand') draft.brands.delete(value);
      else if (kind === 'skin') draft.skins.delete(value);
      else if (kind === 'price') {
        draft.priceMin = null;
        draft.priceMax = null;
      }
      sync();
      return;
    }

    const activeClear = event.target.closest('[data-active-clear]');
    if (activeClear) {
      // Facets only — unlike the panel's Clear, sort and query stay put.
      draft.cat1.clear();
      draft.cat2.clear();
      draft.cat3.clear();
      draft.brands.clear();
      draft.skins.clear();
      draft.priceMin = null;
      draft.priceMax = null;
      sync();
      return;
    }

    const sortOption = event.target.closest('[data-sort]');
    if (sortOption) {
      if (!products) {
        // Server-side fallback while the dataset loads: the anchors carry only
        // ?sort_by=, which would strip q and every filter from the URL — merge
        // the new sort into the state that is already there instead.
        const href = sortOption.getAttribute('href');
        if (!href) return;
        event.preventDefault();
        const sp = new URLSearchParams(location.search);
        sp.delete('sort_by');
        sp.delete('sortBy');
        const sortValue = new URL(href, location.href).searchParams.get('sort_by');
        if (sortValue) sp.set('sort_by', sortValue);
        const qs = sp.toString();
        location.assign(location.pathname + (qs ? `?${qs}` : '') + location.hash);
        return;
      }
      event.preventDefault();
      draft.sort = sortOption.dataset.sort;
      closeSortMenu();
      sync();
      return;
    }

    const sortBtn = event.target.closest('[data-sort-toggle]');
    if (sortBtn) {
      const open = sortMenu && sortMenu.hidden;
      if (open) openSortMenu();
      else closeSortMenu();
      return;
    }

    const viewBtn = event.target.closest('[data-view]');
    if (viewBtn) {
      view = viewBtn.dataset.view;
      try {
        localStorage.setItem('ms_shop_view', view);
      } catch { /* private mode */ }
      applyView();
      return;
    }

    const openBtn = event.target.closest('[data-shop-open]');
    if (openBtn) {
      openSheet(openBtn);
      return;
    }

    const sheetClose = event.target.closest('[data-sheet-close]');
    if (sheetClose) {
      closeSheet(false);
      return;
    }

    const sheetApply = event.target.closest('[data-sheet-apply]');
    if (sheetApply) {
      closeSheet(true);
      return;
    }

    const sheetReset = event.target.closest('[data-sheet-reset]');
    if (sheetReset) {
      draft = emptyState();
      refreshTiles();
      refreshSortUi();
      return;
    }

    const more = event.target.closest('[data-shop-more]');
    if (more && products) {
      event.preventDefault();
      visible += PAGE_SIZE;
      render();
    }
  });

  function openSortMenu() {
    if (!sortMenu) return;
    sortMenu.hidden = false;
    sortToggle?.setAttribute('aria-expanded', 'true');
  }
  function closeSortMenu() {
    if (!sortMenu) return;
    sortMenu.hidden = true;
    sortToggle?.setAttribute('aria-expanded', 'false');
  }
  document.addEventListener('click', (event) => {
    if (sortWrap && !sortWrap.contains(event.target)) closeSortMenu();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeSortMenu();
    if (sheetOpen) closeSheet(false);
  });

  // ---------- bottom sheet ----------
  let sheetSnapshot = null;
  // The sheet is aria-modal, so it holds keyboard focus like the site's
  // other panels (snippets/focus-trap.liquid): focus moves into it on open,
  // Tab stays inside, and closing returns focus to the Filter button.
  let sheetTrap = null;
  function openSheet(opener) {
    if (!sheetEl) return;
    sheetSnapshot = cloneState(draft);
    sheetOpen = true;
    sheetEl.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    const panel = $('[role="dialog"]', sheetEl);
    if (sheetTrap) sheetTrap.release({ restore: false });
    if (panel && window.MSFocusTrap) sheetTrap = window.MSFocusTrap.activate(panel, { returnTo: opener });
  }
  function closeSheet(applyChanges) {
    if (!sheetEl) return;
    const snapshot = sheetSnapshot;
    sheetSnapshot = null;
    sheetOpen = false;
    sheetEl.hidden = true;
    document.documentElement.style.overflow = '';
    if (sheetTrap) {
      sheetTrap.release();
      sheetTrap = null;
    }
    if (applyChanges) {
      sync();
      return;
    }
    // Cancel: applied never drifted while the sheet was open (sync early-
    // returns), so just discard the buffer — no re-render, no pagination reset.
    if (snapshot) draft = snapshot;
    refreshTiles();
    refreshSortUi();
  }

  // ---------- panel placement (sidebar <-> sheet) ----------
  const mq = window.matchMedia('(min-width: 900px)');
  function placePanel() {
    if (mq.matches) {
      if (sheetOpen) closeSheet(false);
      if (sidebarEl && panelEl.parentElement !== sidebarEl) sidebarEl.appendChild(panelEl);
    } else if (sheetBody && panelEl.parentElement !== sheetBody) {
      sheetBody.appendChild(panelEl);
    }
  }
  mq.addEventListener('change', placePanel);
  placePanel();

  // ---------- facet availability ----------
  // Legacy scopes the brand and skin-type OPTION LISTS to the selected
  // category (GET /categories/{id}/brands|skin-types): options with no
  // products in the category disappear, an empty section hides entirely,
  // and stale selections are pruned when the category changes.
  function refreshFacetOptions() {
    if (!products) return;
    const cats = activeCategoryHandles(draft);
    const scope = cats.size
      ? products.filter((p) => p.collections.some((h) => cats.has(h)))
      : products;

    const vendors = new Set(scope.map((p) => (p.vendor || '').toLowerCase()));
    let anyBrand = false;
    brandTiles.forEach((tile) => {
      const show = vendors.has(tile.dataset.vendor.toLowerCase());
      tile.hidden = !show;
      if (show) anyBrand = true;
      else draft.brands.delete(tile.dataset.shopBrand); // stale selection
    });
    const brandSection = $('[data-facet="brand"]', panelEl);
    if (brandSection) brandSection.hidden = !anyBrand;
    visibleBrandSlugs = brandTiles.filter((t) => !t.hidden).map((t) => t.dataset.shopBrand);

    const present = new Set();
    scope.forEach((p) => (SKIN_MAP[p.handle] || []).forEach((s) => present.add(s)));
    let anySkin = false;
    skinTiles.forEach((tile) => {
      const show = present.has(tile.dataset.shopSkin);
      tile.hidden = !show;
      if (show) anySkin = true;
      else draft.skins.delete(tile.dataset.shopSkin); // stale selection
    });
    const skinSection = $('[data-facet="skin"]', panelEl);
    if (skinSection) skinSection.hidden = !anySkin;
    visibleSkinSlugs = skinTiles.filter((t) => !t.hidden).map((t) => t.dataset.shopSkin);
  }

  // ---------- boot ----------
  applyView();
  refreshSortUi();
  refreshTiles();

  if (hasActiveState(applied)) {
    grid.hidden = true;
    if (moreBtn) moreBtn.hidden = true;
    if (emptyEl) emptyEl.hidden = true;
    loadingEl.hidden = false;
  }

  loadData()
    .then((data) => {
      products = data;
      // refreshTiles prunes deep-linked selections that don't exist in the
      // category scope (stale brand/skin) — recommit so applied matches.
      refreshTiles();
      if (sheetOpen) {
        // The sheet opened before the dataset arrived: anything picked in it
        // stays buffered until Apply — render the URL state we booted from.
        render();
      } else {
        // A filter clicked while the dataset was loading is a real user
        // commit: push it as its own entry so Back returns to the landing
        // state. A plain boot only canonicalizes (replaceState).
        urlPushEnabled = preDataCommit;
        applied = cloneState(draft);
        render();
      }
      urlPushEnabled = true;
    })
    .catch(() => {
      // Dataset unavailable — restore the server-rendered experience: grid,
      // the paginated See-more anchor, and native tile/sort navigation
      // (their click handlers no-op while products is null).
      loadingEl.hidden = true;
      grid.hidden = false;
      if (moreBtn && moreBtn.hasAttribute('href')) moreBtn.hidden = false;
      // The slider's labels server-render as "Loading..." and only ever get
      // real bounds from the dataset — hide the dead facet.
      const priceSection = $('[data-facet="price"]', panelEl);
      if (priceSection) priceSection.hidden = true;
    });
})();
